<template>
  <div class="zones-tab">
    <div class="zones-header">
      <Button
        v-if="canEdit"
        type="primary"
        icon="md-add"
        @click="openCreate()"
      >
        {{ $t("Add zone") }}
      </Button>
    </div>

    <Spin
      v-if="organizationStore.zonesLoading"
      fix
    />

    <!-- Zone cards -->
    <div
      v-if="rows.length"
      class="zones-grid"
    >
      <div
        v-for="zone in rows"
        :key="zone.id"
        class="zone-card"
        @click="openDetail(zone)"
      >
        <!-- Square world-map thumbnail on the left -->
        <div class="zone-card__map-wrap">
          <ZoneMap
            :key="'thumb-' + zone.id"
            :countries="zone.countries || []"
            :interactive="false"
            square
            class="zone-card__map"
          />
        </div>

        <!-- Content on the right -->
        <div class="zone-card__body">
          <div class="zone-card__head">
            <div class="zone-card__title">
              <p class="zone-card__name">
                {{ zone.name }}
              </p>
              <p class="zone-card__entity">
                <Icon type="md-git-network" />
                {{ organizationStore.entityNameById(zone.entity) }}
              </p>
            </div>
            <span
              v-if="canEdit"
              class="zone-card__actions"
              @click.stop
            >
              <button
                type="button"
                class="zone-card__action"
                :title="$t('Edit')"
                @click="openEdit(zone)"
              >
                <Icon type="md-create" />
              </button>
              <button
                type="button"
                class="zone-card__action zone-card__action--danger"
                :title="$t('Delete')"
                @click="confirmDelete(zone)"
              >
                <Icon type="md-trash" />
              </button>
            </span>
          </div>

          <div class="zone-card__flags">
            <img
              v-for="c in flagPreview(zone.countries)"
              :key="c.iso"
              :src="c.flag"
              :alt="c.display_name"
              :title="c.display_name"
              class="zone-card__flag"
            >
            <span
              v-if="(zone.countries || []).length > FLAG_PREVIEW"
              class="zone-card__more"
            >
              +{{ (zone.countries || []).length - FLAG_PREVIEW }}
            </span>
          </div>

          <div class="zone-card__footer">
            <span class="zone-card__count">
              {{ (zone.countries || []).length }} {{ $t("countries") }}
            </span>
            <span class="zone-card__view">{{ $t("View details") }} ›</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-else-if="!organizationStore.zonesLoading"
      class="zones-empty"
    >
      <Icon
        type="md-globe"
        class="zones-empty__icon"
      />
      <p>{{ $t("No zone yet") }}</p>
    </div>

    <!-- Zone detail modal -->
    <Modal
      v-model="detail.show"
      :title="detail.zone ? detail.zone.name : ''"
      :width="zoneModalWidth"
      footer-hide
    >
      <div
        v-if="detail.zone"
        class="zd"
      >
        <div class="zd__meta">
          <span class="zd__entity">
            <Icon type="md-git-network" />
            {{ organizationStore.entityNameById(detail.zone.entity) }}
          </span>
          <span class="zd__count">
            {{ (detail.zone.countries || []).length }} {{ $t("countries") }}
          </span>
        </div>

        <ZoneMap
          v-if="detail.show"
          :key="detail.zone.id"
          :countries="detail.zone.countries || []"
          class="zd__map"
        />

        <Input
          v-model="detailSearch"
          search
          clearable
          :placeholder="$t('Search a country')"
          class="zd__search"
        />

        <div class="zd__continents">
          <div
            v-for="grp in detailGroups"
            :key="grp.code"
            class="zd__group"
          >
            <p class="zd__group-title">
              {{ $t(grp.label) }} <span class="zd__group-count">{{ grp.countries.length }}</span>
            </p>
            <div class="zd__group-flags">
              <span
                v-for="c in grp.countries"
                :key="c.iso"
                class="zd__country"
              >
                <img
                  :src="c.flag"
                  :alt="c.display_name"
                  class="zd__flag"
                >
                {{ c.display_name }}
              </span>
            </div>
          </div>
          <div
            v-if="!detailGroups.length"
            class="zones-empty"
          >
            {{ $t("No country found") }}
          </div>
        </div>
      </div>
    </Modal>

    <!-- Create / edit zone modal (custom footer so validation errors keep it open) -->
    <Modal
      v-model="modal.show"
      :title="modal.id ? $t('Edit zone') : $t('New zone')"
      :width="zoneModalWidth"
      footer-hide
    >
      <Form :label-width="110">
        <FormItem
          :label="$t('Name')"
          :error="errors.name"
        >
          <Input
            v-model="modal.name"
            :placeholder="$t('Zone name')"
            @on-focus="errors.name = ''"
          />
        </FormItem>
        <FormItem
          :label="$t('Entity')"
          :error="errors.entity"
        >
          <Select
            v-model="modal.entity"
            filterable
            :placeholder="$t('Select an entity')"
            @on-change="errors.entity = ''"
          >
            <Option
              v-for="opt in organizationStore.entityOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </Option>
          </Select>
        </FormItem>
        <FormItem :label="$t('Countries')">
          <CountryPicker v-model="modal.countries" />
        </FormItem>
      </Form>
      <div class="zone-modal__footer">
        <Button @click="modal.show = false">
          {{ $t("Cancel") }}
        </Button>
        <Button
          type="primary"
          :loading="saving"
          @click="submit"
        >
          {{ $t("Save") }}
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import CountryPicker from "./CountryPicker.vue";
import ZoneMap from "./ZoneMap.vue";

export default {
  name: "ZonesTab",
  components: { CountryPicker, ZoneMap },
  props: {
    canEdit: { type: Boolean, default: false },
  },
  data() {
    return {
      FLAG_PREVIEW: 8, // how many flags to preview on a card
      modal: { show: false, id: null, name: "", entity: null, countries: [] },
      errors: { name: "", entity: "" },
      saving: false,
      detail: { show: false, zone: null },
      detailSearch: "",
    };
  },
  computed: {
    zoneModalWidth() {
      // Wide modal so more countries fit per row, capped to ~92% of the viewport.
      if (typeof window !== "undefined") {
        return Math.min(1100, Math.round(window.innerWidth * 0.92));
      }
      return 1100;
    },
    rows() {
      return this.organizationStore.zones;
    },
    // Countries of the detailed zone, filtered by the search box, grouped by
    // continent for a readable display.
    detailGroups() {
      if (!this.detail.zone) return [];
      const q = this.detailSearch.trim().toLowerCase();
      const CONTS = [
        { code: "AF", label: "Africa" },
        { code: "EU", label: "Europe" },
        { code: "AS", label: "Asia" },
        { code: "NA", label: "North America" },
        { code: "SA", label: "South America" },
        { code: "OC", label: "Oceania" },
        { code: "AN", label: "Antarctica" },
        { code: "", label: "Other" },
      ];
      const byCont = {};
      (this.detail.zone.countries || []).forEach((iso) => {
        const c = this.countryByIso(iso);
        if (!c) return;
        if (
          q &&
          !(c.display_name || "").toLowerCase().includes(q) &&
          !(c.iso || "").toLowerCase().includes(q)
        ) {
          return;
        }
        const cont = c.continent || "";
        (byCont[cont] = byCont[cont] || []).push(c);
      });
      return CONTS.filter((g) => byCont[g.code] && byCont[g.code].length).map((g) => ({
        ...g,
        countries: byCont[g.code].sort((a, b) =>
          (a.display_name || "").localeCompare(b.display_name || "")
        ),
      }));
    },
  },
  created() {
    // Zones are (re)fetched by the parent Organization view whenever the scoped
    // client changes; fetch here only if the parent hasn't loaded them yet.
    if (!this.organizationStore.zones.length) {
      this.organizationStore.fetchZones();
    }
  },
  methods: {
    countryByIso(iso) {
      return (this.worldInfoStore.countryList || []).find((c) => c.iso === iso) || null;
    },
    flagPreview(countries) {
      // First N countries (with data) for the card preview.
      return (countries || [])
        .map((iso) => this.countryByIso(iso))
        .filter(Boolean)
        .slice(0, this.FLAG_PREVIEW);
    },
    openDetail(zone) {
      this.detail = { show: true, zone };
      this.detailSearch = "";
    },
    openCreate() {
      this.errors = { name: "", entity: "" };
      this.modal = { show: true, id: null, name: "", entity: null, countries: [] };
    },
    openEdit(zone) {
      this.errors = { name: "", entity: "" };
      this.modal = {
        show: true,
        id: zone.id,
        name: zone.name,
        entity: zone.entity,
        countries: [...(zone.countries || [])],
      };
    },
    async submit() {
      // Validate: show per-field errors and KEEP the modal open on failure.
      this.errors = { name: "", entity: "" };
      if (!this.modal.name || !this.modal.name.trim()) {
        this.errors.name = this.$t("Name is required");
      }
      if (!this.modal.entity) {
        this.errors.entity = this.$t("Entity is required");
      }
      if (this.errors.name || this.errors.entity) {
        return; // stay open
      }

      const payload = {
        name: this.modal.name,
        entity: this.modal.entity,
        countries: this.modal.countries,
      };
      this.saving = true;
      try {
        if (this.modal.id) {
          await this.organizationStore.updateZone(this.modal.id, payload);
        } else {
          await this.organizationStore.createZone(payload);
        }
        this.$Message.success(this.$t("Saved"));
        this.modal.show = false; // close only on success
      } catch (err) {
        this.handleApiError(err);
      } finally {
        this.saving = false;
      }
    },
    confirmDelete(zone) {
      this.$Modal.confirm({
        title: this.$t("Delete zone"),
        content: this.$t("Delete this zone?"),
        onOk: async () => {
          try {
            await this.organizationStore.removeZone(zone.id);
            this.$Message.success(this.$t("Zone deleted"));
          } catch (err) {
            this.handleApiError(err);
          }
        },
      });
    },
    handleApiError(err) {
      const data = err && err.response && err.response.data;
      let msg = this.$t("Something went wrong");
      if (data && data.detail) msg = data.detail;
      this.$Message.error(msg);
    },
  },
};
</script>

<style scoped>
.zones-tab {
  position: relative;
}
.zones-header {
  margin-bottom: 16px;
  display: flex;
  justify-content: flex-end;
}

/* ---- Zone cards grid ---- */
.zones-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
@media (max-width: 720px) {
  .zones-grid {
    grid-template-columns: 1fr;
  }
}
.zone-card {
  display: flex;
  align-items: stretch;
  gap: 16px;
  background: #fff;
  border: 1px solid #eef1f5;
  border-radius: 12px;
  padding: 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.zone-card:hover {
  border-color: #d5e8fb;
  box-shadow: 0 4px 14px rgba(45, 140, 240, 0.1);
  transform: translateY(-2px);
}
/* Left: square map thumbnail */
.zone-card__map-wrap {
  flex: 0 0 130px;
  width: 130px;
}
.zone-card__map {
  pointer-events: none; /* the whole card opens the detail */
}
/* Right: content column */
.zone-card__body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
}
.zone-card__head {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}
.zone-card__title {
  flex: 1 1 auto;
  min-width: 0;
}
.zone-card__name {
  font-weight: 600;
  font-size: 15px;
  color: #1f2d3d;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.zone-card__entity {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #808695;
  font-size: 12px;
  margin: 2px 0 0;
}
.zone-card__actions {
  display: inline-flex;
  gap: 2px;
  flex: 0 0 auto;
}
.zone-card__action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  color: #9aa2af;
  cursor: pointer;
  border-radius: 6px;
  transition: background 0.15s ease, color 0.15s ease;
}
.zone-card__action:hover {
  background: #e8f4ff;
  color: #2d8cf0;
}
.zone-card__action--danger:hover {
  background: #ffece8;
  color: #ed4014;
}
.zone-card__flags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 12px 0;
  min-height: 18px;
}
.zone-card__flag {
  width: 24px;
  height: 16px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
}
.zone-card__more {
  font-size: 12px;
  font-weight: 600;
  color: #2d8cf0;
  background: #e8f4ff;
  padding: 1px 8px;
  border-radius: 10px;
}
.zone-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 12px;
  margin-top: auto;
  border-top: 1px solid #f0f2f5;
}
.zone-card__count {
  color: #515a6e;
  font-size: 13px;
  font-weight: 500;
}
.zone-card__view {
  color: #2d8cf0;
  font-size: 12px;
  font-weight: 600;
}

/* ---- Empty state ---- */
.zones-empty {
  text-align: center;
  color: #808695;
  padding: 48px 20px;
}
.zones-empty__icon {
  font-size: 52px;
  color: #dcdee2;
  margin-bottom: 10px;
}

/* ---- Zone detail modal ---- */
.zd__meta {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}
.zd__entity {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #808695;
  font-size: 13px;
}
.zd__count {
  color: #19be6b;
  font-weight: 600;
  font-size: 13px;
}
.zone-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 12px;
  padding-top: 16px;
  border-top: 1px solid #f0f2f5;
}
.zd__map {
  margin-bottom: 16px;
}
.zd__search {
  margin-bottom: 16px;
}
.zd__continents {
  max-height: 56vh;
  overflow-y: auto;
}
.zd__group {
  margin-bottom: 18px;
}
.zd__group-title {
  font-size: 13px;
  font-weight: 600;
  color: #515a6e;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 0 0 8px;
}
.zd__group-count {
  color: #808695;
  background: #f0f2f5;
  padding: 0 6px;
  border-radius: 8px;
  font-size: 11px;
  margin-left: 4px;
}
.zd__group-flags {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 6px;
}
.zd__country {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #1f2d3d;
  padding: 4px 6px;
  border-radius: 6px;
}
.zd__country:hover {
  background: #f7fbff;
}
.zd__flag {
  width: 22px;
  height: 15px;
  object-fit: cover;
  border-radius: 2px;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.06);
  flex: 0 0 auto;
}
</style>
